import { createClient, AnamEvent } from '@anam-ai/js-sdk';
import { normalizeShortAnswer } from '../utils/normalizeShortAnswer';

const InteractionState = {
  INTRO: 'INTRO',
  ASSESSMENT: 'ASSESSMENT',
  REPEAT: 'REPEAT',
  FALLBACK: 'FALLBACK',
  CONSENT: 'CONSENT',
  COMPLETE: 'COMPLETE'
};

const Timing = {
  SPEECH_END_FALLBACK: 8000,
  FIRST_NO_ANSWER_TIMEOUT: 20000,
  SECOND_NO_ANSWER_TIMEOUT: 10000,
  MIN_SPEECH_GAP: 100,
  MIN_QUESTION_GAP: 1000,
  READY_CALLBACK_DELAY: 500
};

const Messages = {
  NO_ANSWER_PROMPT: 'No te escuché, ¿podrías repetir?',
  FINAL_NO_ANSWER: 'No pudimos obtener tu respuesta te invitamos a diligenciar el formulario por la via tradicional.',
  SHORT_EXPRESSIONS: ['Ok', 'Perfecto', 'Te entiendo', 'Claro', 'Dame un momento']
};

const FILLER_WORDS = ['uh', 'um', 'eh', 'ah', 'oh', 'mm', 'hmm', 'mmm', 'ehh', 'ahh', 'ohh'];

const activeServices = new Set();

export const closeAllAnamSessions = async () => {
  console.log(`[ANAM] Closing ${activeServices.size} active session(s)...`);

  const disconnectPromises = Array.from(activeServices).map(async (service) => {
    try {
      if (service && typeof service.disconnect === 'function') {
        await service.disconnect();
      }
    } catch (error) {
      console.error('[ANAM] Error disconnecting service:', error);
    }
  });

  await Promise.all(disconnectPromises);
  activeServices.clear();
};

export class AnamService {
  constructor(callbacks = {}) {
    this.client = null;
    this.sessionToken = '';
    this.callbacks = callbacks;

    this.isReady = false;
    this.isStreaming = false;
    this.audioReady = false;
    this.isDisconnected = false;
    this.isInitializing = false;
    this.isCancelled = false;

    this.isSpeaking = false;
    this.readyForAnswer = false;
    this.speechEndTime = 0;

    this.currentQuestion = null;
    this.questionStartTime = 0;
    this.lastAcceptedAnswerTime = 0;
    this.hasReceivedAnswer = false;


    this.answerResolve = null;
    this.speechEndResolve = null;

    this.speechEndFallbackTimer = null;
    this.assessmentUnmuteTimer = null;
    this.pendingTimerSetup = null;
    this.questionTimeouts = {
      first: null,
      second: null,
      overall: null
    };

    this.currentState = InteractionState.INTRO;
    this.allowInterruption = true;
    this.localAudioStream = null;
    activeServices.add(this);
  }

  async initialize(sessionToken, videoElement) {
    if (this.isDisconnected) {
      throw new Error('Service was disconnected, cannot reinitialize');
    }

    if (this.isInitializing) {
      return;
    }

    if (this.client && this.sessionToken === sessionToken && this.isReady) {
      return;
    }

    this.isInitializing = true;
    this.resetState();
    this.sessionToken = sessionToken;

    try {

      this.client = createClient(sessionToken, {
        personaConfig: {
          voiceDetectionOptions: {
            endOfSpeechSensitivity: 0.2
          },
          language: 'es-CO'
        }
      });
      this.setupEventListeners();

      const videoId = `anam-video-${Date.now()}`;
      videoElement.id = videoId;

      this.localAudioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      await this.client.streamToVideoElement(videoId, this.localAudioStream);
    } catch (error) {
      console.error('[ANAM] Failed to initialize:', error);
      this.isInitializing = false;
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize Anam';
      this.callbacks.onError?.(new Error(errorMessage));

      if (this.localAudioStream) {
        this.localAudioStream.getTracks().forEach(track => track.stop());
        this.localAudioStream = null;
      }

      throw error;
    }
  }

  async askQuestion(question, onMicrophoneStop = null, timeoutSeconds = 15, maxNoAnswerAttempts = 2, isAssessmentQuestion = false) {
    this.validateReadyState();
    this.validateQuestion(question);

    if (onMicrophoneStop) {
      onMicrophoneStop();
    }

    this.prepareForQuestion(question, isAssessmentQuestion ? InteractionState.ASSESSMENT : InteractionState.REPEAT);

    try {
      await this.client.talk(this.currentQuestion);
      return await this.waitForAnswer(timeoutSeconds, maxNoAnswerAttempts);
    } catch (error) {
      this.cleanupQuestionState();
      console.error('[ANAM] Error asking question:', error);
      throw error;
    }
  }

  async sendMessage(message, onMicrophoneStop = null) {
    this.validateReadyState();
    this.validateQuestion(message);

    this.muteMicrophone();

    if (onMicrophoneStop) {
      onMicrophoneStop();
    }

    this.currentQuestion = message.trim();
    this.isSpeaking = true;
    this.readyForAnswer = false;
    this.callbacks.onSpeechStart?.();

    try {
      await this.client.talk(this.currentQuestion);

      return new Promise((resolve) => {
        this.speechEndResolve = resolve;
      });
    } catch (error) {
      this.isSpeaking = false;
      this.speechEndResolve = null;
      console.error('[ANAM] Error sending message:', error);
      throw error;
    }
  }

  async sendIntroMessage(message, onMicrophoneStop = null) {
    this.validateReadyState();
    this.validateQuestion(message);

    this.enterIntro();
    this.muteMicrophone();


    if (onMicrophoneStop) {
      onMicrophoneStop();
    }

    this.currentQuestion = message.trim();
    this.isSpeaking = true;
    this.readyForAnswer = false;
    this.callbacks.onSpeechStart?.();

    try {
      await this.client.talk(this.currentQuestion);

      return new Promise((resolve) => {
        this.speechEndResolve = resolve;
      });
    } catch (error) {
      this.isSpeaking = false;
      this.speechEndResolve = null;
      console.error('[ANAM] Error sending intro message:', error);
      throw error;
    }
  }

  async askAssessmentQuestion(question, onMicrophoneStop = null, timeoutSeconds = 15, maxNoAnswerAttempts = 2, allowInterruption = true) {
    this.validateReadyState();
    this.validateQuestion(question);

    if (onMicrophoneStop) {
      onMicrophoneStop();
    }

    this.prepareForQuestion(question, InteractionState.ASSESSMENT, allowInterruption);

    try {
      await this.client.talk(this.currentQuestion);
      return await this.waitForAnswer(timeoutSeconds, maxNoAnswerAttempts);
    } catch (error) {
      this.cleanupQuestionState();
      console.error('[ANAM] Error asking assessment question:', error);
      throw error;
    }
  }

  async askConsentQuestion(question, onMicrophoneStop = null, timeoutSeconds = 15, maxNoAnswerAttempts = 2) {
    this.validateReadyState();
    this.validateQuestion(question);

    if (onMicrophoneStop) {
      onMicrophoneStop();
    }

    this.prepareForQuestion(question, InteractionState.CONSENT);

    try {
      await this.client.talk(this.currentQuestion);
      return await this.waitForAnswer(timeoutSeconds, maxNoAnswerAttempts);
    } catch (error) {
      this.cleanupQuestionState();
      console.error('[ANAM] Error asking consent question:', error);
      throw error;
    }
  }

  async sendFallbackMessage(message, onMicrophoneStop = null) {
    this.validateReadyState();
    this.validateQuestion(message);

    this.enterFallback();
    this.muteMicrophone();


    if (onMicrophoneStop) {
      onMicrophoneStop();
    }

    this.currentQuestion = message.trim();
    this.isSpeaking = true;
    this.readyForAnswer = false;
    this.callbacks.onSpeechStart?.();

    try {
      await this.client.talk(this.currentQuestion);

      return new Promise((resolve) => {
        this.speechEndResolve = resolve;
      });
    } catch (error) {
      this.isSpeaking = false;
      this.speechEndResolve = null;
      console.error('[ANAM] Error sending fallback message:', error);
      throw error;
    }
  }

  async sendShortExpression() {
    const expressions = Messages.SHORT_EXPRESSIONS;
    const randomExpression = expressions[Math.floor(Math.random() * expressions.length)];

    if (this.client && this.isStreaming && this.audioReady) {
      try {
        this.muteMicrophone();
        this.isSpeaking = true;
        await this.client.talk(randomExpression);

        return new Promise((resolve) => {
          this.speechEndResolve = resolve;
        });
      } catch (error) {
        console.error('[ANAM] Error sending short expression:', error);
        this.isSpeaking = false;
      }
    }
  }

  async disconnect() {
    if (this.isDisconnected) {
      return;
    }

    this.isDisconnected = true;
    this.isCancelled = true;
    activeServices.delete(this);
    this.clearAllTimersAggressive();

    if (this.localAudioStream) {
      try {
        this.localAudioStream.getTracks().forEach(track => track.stop());
        this.localAudioStream = null;
      } catch (e) {
        console.error('[ANAM] Error stopping local tracks:', e);
      }
    }

    if (this.client) {
      try {
        await this.client.stopStreaming();
      } catch (error) {
        console.error('[ANAM] Error stopping stream:', error);
      } finally {
        this.resetState();
        this.client = null;
      }
    }
  }

  isConnected() {
    return this.isStreaming && this.isReady && this.audioReady;
  }

  isCurrentlySpeaking() {
    return this.isSpeaking;
  }

  muteMicrophone() {
    if (this.client && this.isStreaming) {
      try {
        this.client.muteInputAudio();
        this.callbacks.onMicStateChange?.(true);
      } catch (error) {
        console.error('[ANAM] Error muting microphone:', error);
      }
    }
  }

  unmuteMicrophone() {
    if (this.client && this.isStreaming) {
      try {
        this.client.unmuteInputAudio();
        this.callbacks.onMicStateChange?.(false);
      } catch (error) {
        console.error('[ANAM] Error unmuting microphone:', error);
      }
    }
  }

  getMicrophoneState() {
    if (this.client && this.isStreaming) {
      try {
        return this.client.getInputAudioState();
      } catch (error) {
        console.error('[ANAM] Error getting microphone state:', error);
        return null;
      }
    }
    return null;
  }

  isMicrophoneMuted() {
    const state = this.getMicrophoneState();
    return state ? state.isMuted : null;
  }

  setMicEnabled(enabled) {

    if (enabled) {
      this.unmuteMicrophone();
    } else {
      this.muteMicrophone();
    }
  }

  shouldAllowInterruption() {


    if (!this.allowInterruption) {
      return false;
    }

    return [
      InteractionState.ASSESSMENT,
      InteractionState.REPEAT,
      InteractionState.CONSENT
    ].includes(this.currentState);
  }

  shouldUnmuteAfterSpeech() {

    return [
      InteractionState.ASSESSMENT,
      InteractionState.REPEAT,
      InteractionState.CONSENT
    ].includes(this.currentState);
  }

  setupEventListeners() {
    if (!this.client) {
      return;
    }

    this.client.addListener(AnamEvent.CONNECTION_ESTABLISHED, () => {
      this.isReady = true;
    });

    this.client.addListener(AnamEvent.SESSION_READY, () => {
      this.isReady = true;
    });

    this.client.addListener(AnamEvent.VIDEO_STREAM_STARTED, () => {
      this.isStreaming = true;
      this.isInitializing = false;

      setTimeout(() => {
        this.callbacks.onReady?.();
      }, Timing.READY_CALLBACK_DELAY);
    });

    this.client.addListener(AnamEvent.AUDIO_STREAM_STARTED, () => {
      this.audioReady = true;
    });

    this.client.addListener(AnamEvent.MESSAGE_STREAM_EVENT_RECEIVED, (event) => {
      this.handleMessageStreamEvent(event);
    });

    this.client.addListener(AnamEvent.MESSAGE_HISTORY_UPDATED, () => {

    });

    this.client.addListener(AnamEvent.CONNECTION_CLOSED, () => {
      this.isReady = false;
      this.isStreaming = false;
      this.audioReady = false;
    });
  }

  handleMessageStreamEvent(event) {
    if (!event) {
      return;
    }

    if (event.role === 'persona' && event.endOfSpeech === true) {
      this.handleAvatarSpeechEnd();
    }

    if (event.role === 'persona' && event.content && !this.isSpeaking) {
      this.handleAvatarSpeechStart();
    }

    if (event.role === 'user' && event.endOfSpeech === true && event.content) {
      this.handleUserMessage(event.content);
    }
  }

  handleAvatarSpeechEnd() {
    if (this.isCancelled) return;

    console.log('[ANAM] Event: Avatar STOPPED speaking');
    this.isSpeaking = false;
    this.speechEndTime = Date.now();



    if (this.shouldUnmuteAfterSpeech()) {
      this.readyForAnswer = true;

      this.unmuteMicrophone();
    } else {

      this.readyForAnswer = false;
    }

    if (this.pendingTimerSetup) {
      this.pendingTimerSetup();
      this.pendingTimerSetup = null;
    }

    this.clearTimer('speechEndFallback');
    this.callbacks.onSpeechEnd?.();

    if (this.speechEndResolve) {
      const resolve = this.speechEndResolve;
      this.speechEndResolve = null;
      resolve();
    }
  }

  handleAvatarSpeechStart() {
    console.log('[ANAM] Event: Avatar STARTED speaking');
    this.isSpeaking = true;


    if (this.shouldAllowInterruption()) {
      this.unmuteMicrophone();
      this.readyForAnswer = true;
    } else {
      this.muteMicrophone();
    }

    this.callbacks.onSpeechStart?.();
  }

  handleUserMessage(userMessage) {
    const trimmedMessage = userMessage.trim();
    const normalizedMessage = normalizeShortAnswer(trimmedMessage);
    console.log('[ANAM] Event: User SPOKE:', trimmedMessage);

    if (!this.shouldAcceptUserMessage(normalizedMessage)) {
      return;
    }

    this.acceptUserAnswer(normalizedMessage);
  }

  shouldAcceptUserMessage(message) {
    const micState = this.getMicrophoneState();
    if (micState?.isMuted === true) {
      return false;
    }

    if (!this.isValidAnswer(message)) {
      return false;
    }


    if (this.isSpeaking && !this.shouldAllowInterruption()) {
      return false;
    }

    return true;
  }

  acceptUserAnswer(answer) {
    if (this.isCancelled) return;

    if (!this.answerResolve || typeof this.answerResolve !== 'function') {
      return;
    }

    this.lastAcceptedAnswerTime = Date.now();
    this.hasReceivedAnswer = true;
    this.clearQuestionTimeouts();


    const resolve = this.answerResolve;
    this.answerResolve = null;
    if (!this.isCancelled) {
      resolve(answer);
    }
  }

  isValidAnswer(message) {
    if (!message || typeof message !== 'string') {
      return false;
    }

    const trimmed = message.trim();

    if (trimmed.length === 0) {
      return false;
    }

    const lowerTrimmed = trimmed.toLowerCase();

    if (FILLER_WORDS.includes(lowerTrimmed)) {
      return false;
    }

    return true;
  }

  validateReadyState() {
    if (!this.client) {
      throw new Error('Anam service not initialized');
    }

    if (!this.isStreaming) {
      throw new Error('Video stream not started');
    }

    if (!this.audioReady) {
      throw new Error('Audio stream not ready');
    }
  }

  validateQuestion(question) {
    if (!question || question.trim().length === 0) {
      throw new Error('Question cannot be empty');
    }
  }

  prepareForQuestion(question, state = InteractionState.ASSESSMENT, allowInterruption = true) {
    if (this.answerResolve) {
      const resolve = this.answerResolve;
      this.answerResolve = null;
      resolve('');
    }

    this.clearAllTimersAggressive();



    switch (state) {
      case InteractionState.ASSESSMENT:
        this.enterAssessment();
        break;
      case InteractionState.CONSENT:
        this.enterConsent();
        break;
      case InteractionState.REPEAT:
        this.enterRepeat();
        break;
      default:
        this.currentState = state;
    }

    this.currentQuestion = question.trim();
    this.allowInterruption = allowInterruption;
    this.questionStartTime = Date.now();
    this.lastAcceptedAnswerTime = 0;
    this.hasReceivedAnswer = false;



  }

  async waitForAnswer(timeoutSeconds, maxNoAnswerAttempts) {
    this.clearTimer('speechEndFallback');

    return new Promise((resolve) => {
      let hasAnswered = false;
      let noAnswerAttempts = 0;

      this.answerResolve = (answer) => {
        if (hasAnswered) {
          return;
        }
        hasAnswered = true;
        this.clearQuestionTimeouts();
        this.answerResolve = null;
        this.hasReceivedAnswer = false;
        resolve(answer);
      };

      this.setupSpeechEndFallback();
      this.setupNoAnswerTimeouts(resolve, hasAnswered, noAnswerAttempts, maxNoAnswerAttempts, timeoutSeconds);
    });
  }

  setupSpeechEndFallback() {
    if (this.isSpeaking) {
      this.speechEndFallbackTimer = setTimeout(() => {
        if (this.isSpeaking && this.currentState === InteractionState.REPEAT) {
          this.isSpeaking = false;
          this.speechEndTime = Date.now();
          this.readyForAnswer = true;
        }
      }, Timing.SPEECH_END_FALLBACK);
    }
  }

  setupNoAnswerTimeouts(resolve, hasAnswered, noAnswerAttempts, maxAttempts, timeoutSeconds) {
    const waitForSpeechEnd = () => new Promise((speechResolve) => {
      const speechTimeout = setTimeout(() => {
        speechResolve();
      }, Timing.SPEECH_END_FALLBACK);

      const originalResolve = this.speechEndResolve;
      this.speechEndResolve = () => {
        clearTimeout(speechTimeout);
        originalResolve?.();
        speechResolve();
      };
    });

    const finalizeNoAnswer = async (reason) => {
      if (hasAnswered || this.hasReceivedAnswer) {
        return;
      }

      hasAnswered = true;
      this.clearQuestionTimeouts();
      this.answerResolve = null;
      this.readyForAnswer = false;
      this.hasReceivedAnswer = false;
      this.clearTimer('speechEndFallback');

      try {
        this.enterFallback();
        await this.client.talk(Messages.FINAL_NO_ANSWER);
        await waitForSpeechEnd();
      } catch (error) {
        console.error('[ANAM] Error delivering final no-answer message:', error);
      } finally {
        resolve(null);
      }
    };

    this.pendingTimerSetup = () => {
      console.log('[ANAM] Starting no-answer timeout after avatar finished speaking');
      this.questionTimeouts.first = setTimeout(async () => {
        if (hasAnswered || this.hasReceivedAnswer) {
          return;
        }

        noAnswerAttempts++;
        console.log(`[ANAM] First no-answer timeout triggered (attempt ${noAnswerAttempts}/${maxAttempts})`);

        if (noAnswerAttempts >= maxAttempts) {
          await finalizeNoAnswer('first_timeout');
          return;
        }

        try {
          this.enterRepeat();
          await this.client.talk(Messages.NO_ANSWER_PROMPT);
          await waitForSpeechEnd();

          this.clearTimer('questionTimeouts.second');

          this.questionTimeouts.second = setTimeout(async () => {
            if (hasAnswered || this.hasReceivedAnswer) {
              return;
            }

            noAnswerAttempts++;
            console.log(`[ANAM] Second no-answer timeout triggered (attempt ${noAnswerAttempts}/${maxAttempts})`);

            if (noAnswerAttempts >= maxAttempts) {
              await finalizeNoAnswer('second_timeout');
            }
          }, Timing.SECOND_NO_ANSWER_TIMEOUT);
        } catch (error) {
          console.error('[ANAM] Error prompting user:', error);
          await finalizeNoAnswer('prompt_error');
        }
      }, Timing.FIRST_NO_ANSWER_TIMEOUT);
    };

    // Always wait for avatar endOfSpeech before starting timeout - never start immediately.
    // This prevents the timeout from firing while the avatar is still reading a long question
    // (client.talk() returns before the avatar finishes, so isSpeaking may be false initially).
    // pendingTimerSetup is called in handleAvatarSpeechEnd when persona endOfSpeech arrives.
    console.log('[ANAM] No-answer timeout will start after avatar finishes speaking');
  }

  enterIntro() {
    this.clearAllTimersAggressive();
    this.currentState = InteractionState.INTRO;
    this.muteMicrophone();
    this.readyForAnswer = false;
  }

  enterAssessment() {
    this.clearAllTimersAggressive();
    this.currentState = InteractionState.ASSESSMENT;
  }

  enterRepeat() {
    this.clearAllTimersAggressive();
    this.currentState = InteractionState.REPEAT;
  }

  enterFallback() {
    this.clearAllTimersAggressive();
    this.currentState = InteractionState.FALLBACK;
  }

  enterConsent() {
    this.clearAllTimersAggressive();
    this.currentState = InteractionState.CONSENT;
  }

  enterComplete() {
    this.clearAllTimersAggressive();
    this.currentState = InteractionState.COMPLETE;
    this.readyForAnswer = false;
  }

  clearQuestionTimeouts() {
    this.clearTimer('questionTimeouts.first');
    this.clearTimer('questionTimeouts.second');
    this.clearTimer('questionTimeouts.overall');
  }

  clearAllTimers() {
    this.clearQuestionTimeouts();
    this.clearTimer('speechEndFallback');
    this.clearTimer('assessmentUnmute');
  }

  clearAllTimersAggressive() {
    this.clearQuestionTimeouts();
    this.clearTimer('speechEndFallback');
    this.clearTimer('assessmentUnmute');
    this.pendingTimerSetup = null;
  }

  clearTimer(timerPath) {
    const parts = timerPath.split('.');
    let obj = this;

    for (let i = 0; i < parts.length - 1; i++) {
      obj = obj[parts[i]];
      if (!obj) {
        return;
      }
    }

    const timerName = parts[parts.length - 1];
    const timer = obj[timerName];

    if (timer) {
      clearTimeout(timer);
      obj[timerName] = null;
    }
  }

  resetState() {
    this.isReady = false;
    this.isStreaming = false;
    this.audioReady = false;
    this.isSpeaking = false;
    this.readyForAnswer = false;
    this.speechEndTime = 0;
    this.currentQuestion = null;
    this.questionStartTime = 0;
    this.lastAcceptedAnswerTime = 0;
    this.hasReceivedAnswer = false;

    this.answerResolve = null;
    this.speechEndResolve = null;
    this.currentState = InteractionState.INTRO;
    this.clearAllTimersAggressive();
  }

  cleanupQuestionState() {
    this.isSpeaking = false;
    this.answerResolve = null;
    this.clearAllTimers();
  }
}
